import React from 'react';
import { Box } from "@chakra-ui/react";
import { ReactSVG } from 'react-svg';
import { RatingEmojiProps, getRatingGlowColor } from '@/utils/restaurantUtils';

interface Props {
  emoji: RatingEmojiProps;
  size?: "sm" | "md" | "lg" | "xl" | "2xl" | "3xl";
  /** Show the compatibility color as a circular background behind the emoji */
  showBackground?: boolean;
  /** Custom background color (overrides auto-detection from emoji value) */
  backgroundColor?: string;
  /**
   * Ring diameter as a multiple of the emoji's dimension (only applies when
   * `showBackground` is true). Defaults to 1.2 — the original ratio used by
   * every existing call site. Pass a smaller value (closer to 1) to make the
   * emoji fill more of its ring, e.g. for a call site that wants a tighter,
   * more prominent glyph within the color ring.
   */
  ringMultiplier?: number;
}

const sizes = {
  sm: '16px',
  md: '20px',
  lg: '24px',
  xl: '32px',
  '2xl': '48px',
  '3xl': '64px',
};

export default function RatingEmoji({
  emoji,
  size = "lg",
  showBackground = false,
  backgroundColor,
  ringMultiplier = 1.2,
}: Props) {
  const dimension = sizes[size];

  // Always set both width and height to ensure consistent button sizing
  const svgSize = `width: ${dimension}; height: ${dimension};`;

  // Calculate background size (larger than emoji)
  const bgDimension = `calc(${dimension} * ${ringMultiplier})`;

  // Get background color from emoji value if not provided
  const bgColor = backgroundColor ?? getRatingGlowColor(emoji.value);

  if (showBackground) {
    return (
      <Box
        as="span"
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        width={bgDimension}
        height={bgDimension}
        borderRadius="full"
        bg={bgColor}
        boxShadow={`0 4px 12px ${bgColor}40`}
        transition="all 0.2s ease-out"
      >
        <Box
          as="span"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          width={dimension}
          height={dimension}
        >
          <ReactSVG
            src={emoji.icon}
            beforeInjection={(svg) => {
              svg.setAttribute('style', svgSize);
              svg.setAttribute('viewBox', svg.getAttribute('viewBox') || '0 0 24 24');
            }}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      justifyContent="center"
      width={dimension}
      height={dimension}
    >
      <ReactSVG
        src={emoji.icon}
        beforeInjection={(svg) => {
          svg.setAttribute('style', svgSize);
          // Ensure the SVG fills the container properly
          svg.setAttribute('viewBox', svg.getAttribute('viewBox') || '0 0 24 24');
        }} />
    </Box>
  );
}
